/**
 * 拖拽文件导入（DESIGN.md §2.6/§6.1 补充）。
 *
 * 从桌面拖一张图片到页面：已经开着文档就当"置入"处理——交给
 * `PlaceToolController` 开一个可交互的摆放会话（等比缩放到不超过画布、
 * 居中，拖手柄调大小，Enter 才真正落盘），不在这里直接写像素；没有文档
 * 打开时退化成"打开"，直接复用 documentFromImage（跟 file-actions.ts 的
 * openFromGateway 走同一条路）。
 *
 * 只接受本应用文件对话框同样支持的位图格式——拖一个 .txt 或文件夹进来
 * 不该崩，静默忽略（返回 false，调用方决定要不要提示）。
 */
import { decodeImage, type DecodedImage } from '../io/codecs';
import type { TileStore } from '../engine/tile/store';
import { documentFromImage } from './file/image-import';
import type { AppStore } from './store';

export interface DropImportDeps {
  readonly tiles: TileStore;
  readonly now: () => string;
  /** 已经开着文档时调用——交给置入会话处理，不在这里直接写像素。 */
  readonly beginPlace: (fileName: string, image: DecodedImage, docWidth: number, docHeight: number) => void;
  /** 注入点：测试给假实现，不必依赖真的 wasm 编解码模块（同 FileDeps 的做法）。 */
  readonly decode?: typeof decodeImage | undefined;
}

const ACCEPTED_NAME = /\.(png|jpe?g)$/i;

/** 拖进来的文件是不是这条路径认得的位图格式——按文件名，跟文件对话框的 accept 列表一致。 */
export function isDroppableImage(fileName: string): boolean {
  return ACCEPTED_NAME.test(fileName);
}

/**
 * 处理一次拖放的文件。返回 true 表示已处理（新开文档或者启动了置入会话）；
 * 格式不认得时返回 false，什么都不做。
 */
export async function importDroppedFile(
  store: AppStore,
  file: { readonly bytes: Uint8Array; readonly name: string },
  deps: DropImportDeps,
): Promise<boolean> {
  if (!isDroppableImage(file.name)) return false;
  const decode = deps.decode ?? decodeImage;
  const image = await decode(file.bytes);

  const document = store.document;
  if (document === null) {
    const opened = documentFromImage(image, deps.tiles, {
      id: `doc-${deps.now()}`,
      fileName: file.name,
      now: deps.now,
    });
    store.setDocument(opened);
    return true;
  }

  deps.beginPlace(file.name, image, document.size.width, document.size.height);
  return true;
}
